import { useState, useEffect } from 'react'
import firebase from '../firebase/compat'
import { cachedSectorPaths } from '../helpers/intelligence/sectorPathsCache'
import callWithColdStartRetry from '../helpers/intelligence/callWithColdStartRetry'

/**
 * De TOC-paden van één thema: alleen paden die door de doelgroep én het
 * effect lopen, server-side gefilterd (sectorTocPaths met `filter`).
 *
 * Het antwoord moet `filtered` dragen. Een functie van vóór het filter
 * negeert de parameter en geeft het hele sectormodel terug; dat als 'de paden
 * van dit thema' tonen zou onwaar zijn, dus dat is hier een fout en geen
 * model.
 * AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
 *
 * Via de cache en met koude-start-herkansing, net als de sectorverkenner: de
 * scan leest cross-tenant en de functie schaalt naar nul.
 *
 * @param {{id: string, msiId: string, targetgroup: string, sectors?: Array<string>}|null} theme
 * @returns {{model: (object|null), loading: boolean, loaded: boolean, error: (Error|null)}}
 */
const useThemeTocPaths = (theme) => {
    const [model, setModel] = useState(/** @type {object|null} */(null))
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState(/** @type {Error|null} */(null))

    const themeId = theme && typeof theme.id === 'string' ? theme.id : ''
    const msiId = (theme && theme.msiId) || ''
    const targetgroup = (theme && theme.targetgroup) || ''
    const sector = (theme && Array.isArray(theme.sectors) && theme.sectors[0]) || ''

    useEffect(() => {
        if (!themeId || !msiId || !targetgroup) return undefined
        let cancelled = false
        setLoaded(false)
        setError(null)
        setModel(null)

        const filter = { targetgroup, msiId }
        cachedSectorPaths(`thema|${themeId}`, () => {
            const callable = firebase.app().functions('europe-west1').httpsCallable('sectorTocPaths')
            return callWithColdStartRetry(callable, { sector, msiIds: [msiId], filter })
        })
            .then((res) => {
                if (cancelled) return
                const data = (res && res.data) || {}
                if (!data.filtered || data.filtered.msiId !== msiId || data.filtered.targetgroup !== targetgroup) {
                    throw new Error('De padfunctie kent het themafilter nog niet; sectorTocPaths moet opnieuw uitgerold worden.')
                }
                setModel(data)
                setLoaded(true)
            })
            .catch((err) => {
                if (cancelled) return
                console.error('useThemeTocPaths:', err)
                setError(err)
                setModel(null)
                setLoaded(false)
            })

        return () => { cancelled = true }
    }, [themeId, msiId, targetgroup, sector])

    return { model, loading: !loaded && error === null, loaded, error }
}

export default useThemeTocPaths
